import React, { useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Alert,
  InputGroup,
  Spinner,
} from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { FiClock, FiEdit2, FiSave, FiUser, FiCpu, FiBook } from 'react-icons/fi';
import { configApi, systemConfigApi, SystemConfig } from '../services/api';
import { SLAConfig } from '../types';
import { LoadingSpinner } from '../components/common';
import { useReadingMode } from '../contexts/ReadingModeContext';
import { useToast } from '../contexts/ToastContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Available Polly voices
const POLLY_VOICES = [
  { id: 'Andres', label: 'Andres (Mexico)' },
  { id: 'Mia', label: 'Mia (Mexico)' },
  { id: 'Lucia', label: 'Lucia (Spain)' },
  { id: 'Conchita', label: 'Conchita (Spain)' },
  { id: 'Enrique', label: 'Enrique (Spain)' },
  { id: 'Lupe', label: 'Lupe (US)' },
  { id: 'Pedro', label: 'Pedro (US)' },
];

const Config: React.FC = () => {
  // SLA State
  const [globalSLA, setGlobalSLA] = useState<SLAConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingGlobal, setEditingGlobal] = useState(false);
  const [newGlobalSLA, setNewGlobalSLA] = useState<number>(72);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // System Config State
  const [systemConfig, setSystemConfig] = useState<SystemConfig | null>(null);
  const [aiModels, setAIModels] = useState<string[]>([]);
  const [loadingSystemConfig, setLoadingSystemConfig] = useState(true);
  const [savingTTS, setSavingTTS] = useState(false);
  const [savingAIModel, setSavingAIModel] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);

  // Admin check - for now use header, later integrate with auth context
  const [isAdmin] = useState(true); // TODO: Get from auth context

  // Reading mode
  const { isReadingMode, toggleReadingMode } = useReadingMode();
  const toast = useToast();

  const fetchData = async () => {
    try {
      const global = await configApi.getGlobalSLA();
      setGlobalSLA(global);
      setNewGlobalSLA(global.sla_hours);
    } catch (error) {
      console.error('Error fetching config:', error);
      setError('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemConfig = async () => {
    if (!isAdmin) {
      setLoadingSystemConfig(false);
      return;
    }
    try {
      const config = await systemConfigApi.getSystem();
      setSystemConfig(config);
    } catch (error) {
      console.error('Error fetching system config:', error);
      // Silently fail - user may not have admin access
    } finally {
      setLoadingSystemConfig(false);
    }
  };

  const fetchAIModels = async () => {
    setLoadingModels(true);
    try {
      const response = await systemConfigApi.getAIModels();
      setAIModels(response.models);
    } catch (error) {
      console.error('Error fetching AI models:', error);
      toast.error('Error', 'No se pudieron cargar los modelos de IA');
    } finally {
      setLoadingModels(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchSystemConfig();
  }, []);

  // Load AI models when system config is loaded
  useEffect(() => {
    if (systemConfig && isAdmin) {
      fetchAIModels();
    }
  }, [systemConfig, isAdmin]);

  const handleSaveGlobalSLA = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await configApi.updateGlobalSLA(newGlobalSLA, 'auditor-001');
      setGlobalSLA(updated);
      setEditingGlobal(false);
      setSuccess('SLA global actualizado correctamente');
    } catch (error) {
      console.error('Error updating global SLA:', error);
      setError('Error al actualizar el SLA global');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTTS = async (provider: 'polly' | 'browser', voice?: string) => {
    setSavingTTS(true);
    try {
      const voiceToUse = voice || systemConfig?.tts.voice || 'Andres';
      await systemConfigApi.updateTTS(provider, voiceToUse);
      setSystemConfig((prev) => prev ? {
        ...prev,
        tts: { provider, voice: voiceToUse },
      } : null);
      toast.success('Voz actualizada', `Proveedor cambiado a ${provider === 'polly' ? 'Natural' : 'Básica'}`);
    } catch (error) {
      console.error('Error updating TTS:', error);
      toast.error('Error', 'No se pudo actualizar la configuración de voz');
    } finally {
      setSavingTTS(false);
    }
  };

  const handleUpdateVoice = async (voice: string) => {
    if (!systemConfig) return;
    setSavingTTS(true);
    try {
      await systemConfigApi.updateTTS(systemConfig.tts.provider, voice);
      setSystemConfig((prev) => prev ? {
        ...prev,
        tts: { ...prev.tts, voice },
      } : null);
      toast.success('Voz actualizada', `Voz cambiada a ${voice}`);
    } catch (error) {
      console.error('Error updating voice:', error);
      toast.error('Error', 'No se pudo actualizar la voz');
    } finally {
      setSavingTTS(false);
    }
  };

  const handleUpdateAIModel = async (model: string) => {
    setSavingAIModel(true);
    try {
      await systemConfigApi.updateAIModel(model);
      setSystemConfig((prev) => prev ? {
        ...prev,
        ai_model: { model },
      } : null);
      toast.success('Modelo actualizado', `Modelo cambiado a ${model}`);
    } catch (error) {
      console.error('Error updating AI model:', error);
      toast.error('Error', 'No se pudo actualizar el modelo de IA');
    } finally {
      setSavingAIModel(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Cargando configuración..." />;
  }

  return (
    <div className="config-page">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Row className="g-4">
        {/* Global SLA Card */}
        <Col lg={12}>
          <Card>
            <Card.Header className="bg-white">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <FiClock className="me-2" />
                  SLA Global
                </h5>
                {!editingGlobal && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setEditingGlobal(true)}
                  >
                    <FiEdit2 className="me-1" />
                    Editar
                  </Button>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-4">
                El SLA define el tiempo máximo para interpretar los exámenes de los pacientes.
              </p>

              {editingGlobal ? (
                <Form onSubmit={(e) => { e.preventDefault(); handleSaveGlobalSLA(); }}>
                  <Form.Group className="mb-3">
                    <Form.Label>Horas de SLA</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        min={1}
                        max={720}
                        value={newGlobalSLA}
                        onChange={(e) => setNewGlobalSLA(Number(e.target.value))}
                      />
                      <InputGroup.Text>horas</InputGroup.Text>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Valor recomendado: 72 horas (3 días)
                    </Form.Text>
                  </Form.Group>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        setEditingGlobal(false);
                        setNewGlobalSLA(globalSLA?.sla_hours || 72);
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={saving}
                    >
                      <FiSave className="me-1" />
                      {saving ? 'Guardando...' : 'Guardar'}
                    </Button>
                  </div>
                </Form>
              ) : (
                <div>
                  <div className="d-flex align-items-baseline gap-2 mb-3">
                    <h2 className="mb-0">{globalSLA?.sla_hours || 72}</h2>
                    <span className="text-muted">horas</span>
                  </div>
                  {globalSLA?.updated_at && (
                    <small className="text-muted">
                      Última actualización:{' '}
                      {format(new Date(globalSLA.updated_at), 'PPP', { locale: es })}
                      {globalSLA.updated_by && ` por ${globalSLA.updated_by}`}
                    </small>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

      </Row>

      {/* System Configuration - Admin Only */}
      {isAdmin && !loadingSystemConfig && systemConfig && (
        <>
          {/* Voice Configuration */}
          <Card className="mt-4">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <FiUser className="me-2" />
                Voz del Sistema
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-4">
                Selecciona el tipo de voz para la lectura de interpretaciones.
              </p>

              <div className="d-flex gap-4 mb-4">
                {/* Natural Voice (Polly) */}
                <div
                  className="text-center"
                  style={{ cursor: savingTTS ? 'wait' : 'pointer' }}
                  onClick={() => !savingTTS && handleUpdateTTS('polly')}
                >
                  <div
                    className={`d-flex align-items-center justify-content-center mb-2 ${
                      systemConfig.tts.provider === 'polly' ? 'voice-option-selected' : ''
                    }`}
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: 16,
                      backgroundColor: systemConfig.tts.provider === 'polly' ? '#0d6efd' : 'transparent',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <FiUser
                      size={32}
                      color={systemConfig.tts.provider === 'polly' ? '#fff' : '#333'}
                    />
                  </div>
                  <span style={{
                    fontWeight: systemConfig.tts.provider === 'polly' ? 600 : 400,
                    color: '#333'
                  }}>
                    Natural
                  </span>
                </div>

                {/* Basic Voice (Browser) */}
                <div
                  className="text-center"
                  style={{ cursor: savingTTS ? 'wait' : 'pointer' }}
                  onClick={() => !savingTTS && handleUpdateTTS('browser')}
                >
                  <div
                    className={`d-flex align-items-center justify-content-center mb-2 ${
                      systemConfig.tts.provider === 'browser' ? 'voice-option-selected' : ''
                    }`}
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: 16,
                      backgroundColor: systemConfig.tts.provider === 'browser' ? '#0d6efd' : 'transparent',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <FiCpu
                      size={32}
                      color={systemConfig.tts.provider === 'browser' ? '#fff' : '#333'}
                    />
                  </div>
                  <span style={{
                    fontWeight: systemConfig.tts.provider === 'browser' ? 600 : 400,
                    color: '#333'
                  }}>
                    Básica
                  </span>
                </div>
              </div>

              {/* Voice selector - only for Polly */}
              {systemConfig.tts.provider === 'polly' && (
                <Form.Group>
                  <Form.Label>Voz</Form.Label>
                  <Form.Select
                    value={systemConfig.tts.voice}
                    onChange={(e) => handleUpdateVoice(e.target.value)}
                    disabled={savingTTS}
                  >
                    {POLLY_VOICES.map((voice) => (
                      <option key={voice.id} value={voice.id}>
                        {voice.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              {savingTTS && (
                <div className="text-center mt-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Guardando...
                </div>
              )}
            </Card.Body>
          </Card>

          {/* AI Model Configuration */}
          <Card className="mt-4">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <FiCpu className="me-2" />
                Modelo de IA
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-4">
                Selecciona el modelo de inteligencia artificial para interpretar los resultados.
              </p>

              <Form.Group>
                <Form.Label>Modelo actual</Form.Label>
                {loadingModels ? (
                  <div className="d-flex align-items-center gap-2 py-2">
                    <Spinner animation="border" size="sm" />
                    <span className="text-muted">Cargando modelos...</span>
                  </div>
                ) : (
                  <Typeahead
                    id="ai-model-typeahead"
                    options={aiModels}
                    defaultSelected={systemConfig.ai_model.model ? [systemConfig.ai_model.model] : []}
                    onChange={(selected) => {
                      if (selected.length > 0) {
                        handleUpdateAIModel(selected[0] as string);
                      }
                    }}
                    disabled={savingAIModel}
                    placeholder="Buscar modelo..."
                    dropup
                    filterBy={(option, props) => {
                      const model = option as string;
                      const query = props.text.toLowerCase();
                      return model.toLowerCase().includes(query);
                    }}
                    renderMenuItemChildren={(option) => {
                      const model = option as string;
                      return (
                        <div>
                          <span>{model}</span>
                          {model.includes('flash') && (
                            <span className="ms-2 badge bg-success">Rápido</span>
                          )}
                          {model.includes('pro') && (
                            <span className="ms-2 badge bg-primary">Avanzado</span>
                          )}
                        </div>
                      );
                    }}
                  />
                )}
                <Form.Text className="text-muted">
                  Los modelos Flash son más rápidos y económicos. Los modelos Pro son más capaces pero más lentos.
                </Form.Text>
              </Form.Group>

              {savingAIModel && (
                <div className="text-center mt-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Guardando...
                </div>
              )}
            </Card.Body>
          </Card>
        </>
      )}

      {/* Personal Preferences - All Users */}
      <Card className="mt-4">
        <Card.Header className="bg-white">
          <h5 className="mb-0">
            <FiBook className="me-2" />
            Preferencias Personales
          </h5>
        </Card.Header>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h6 className="mb-1">Modo Lectura</h6>
              <p className="text-muted mb-0 small">
                Tipografía más grande, colores suaves, ancho optimizado para lectura.
              </p>
            </div>
            <Form.Check
              type="switch"
              id="reading-mode-switch"
              checked={isReadingMode}
              onChange={toggleReadingMode}
              className="fs-4"
            />
          </div>
        </Card.Body>
      </Card>

    </div>
  );
};

export default Config;
